import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, GraduationCap, Mail, Calendar, Users, Clock, CheckCircle, Crown, User, ArrowRight } from 'lucide-react';
import { API } from '../../App';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const AdminCoachDetail = () => {
  const { coachId } = useParams();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCoach();
  }, [coachId]);

  const fetchCoach = async () => {
    try {
      const res = await API.get(`/admin/coaches/${coachId}`);
      setCoach(res.data);
    } catch (error) {
      console.error('Failed to fetch coach:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!coach) {
    return (
      <div className="text-center py-20">
        <p className="text-[#737373]">Coach not found.</p>
        <Link to="/admin/coaches" className="text-[#2C5530] hover:underline mt-4 inline-block">Back to Coaches</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-coach-detail">
      {/* Back link */}
      <Link to="/admin/coaches" className="inline-flex items-center gap-2 text-[#57534E] hover:text-[#1A1A1A] text-sm transition-colors" data-testid="back-to-coaches">
        <ArrowLeft className="w-4 h-4" /> Back to Coaches
      </Link>

      {/* Coach Header */}
      <div className="bg-white border border-[#E7E5E4] p-6 sm:p-8" data-testid="coach-header">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          <div className="w-16 h-16 bg-[#2C5530] text-white flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-8 h-8" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#1A1A1A] mb-1">{coach.full_name}</h1>
            <p className="text-[#57534E] flex items-center gap-2 mb-3">
              <Mail className="w-4 h-4" /> {coach.email}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {coach.status === 'pending_setup' ? (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-700 text-sm font-medium">
                  <Clock className="w-3.5 h-3.5" /> Pending Setup
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 text-sm font-medium">
                  <CheckCircle className="w-3.5 h-3.5" /> Active
                </span>
              )}
              <span className="flex items-center gap-1 text-sm text-[#57534E]">
                <Users className="w-4 h-4 text-[#737373]" /> {coach.total_clients} student{coach.total_clients !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1 text-sm text-[#737373]">
                <Calendar className="w-4 h-4" /> Joined {formatDate(coach.created_at)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Students List */}
      <div data-testid="coach-students-section">
        <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4">
          Students ({coach.clients?.length || 0})
        </h2>

        {coach.clients && coach.clients.length > 0 ? (
          <div className="space-y-3">
            {coach.clients.map((client) => (
              <Link
                key={client.id}
                to={`/admin/clients/${client.id}`}
                className="flex items-center justify-between bg-white border border-[#E7E5E4] p-4 sm:p-5 hover:border-[#2C5530]/30 hover:shadow-sm transition-all"
                data-testid={`student-${client.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-[#F2F0E9] text-[#57534E] flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1A1A1A] truncate">{client.full_name}</p>
                    <p className="text-sm text-[#737373] truncate">{client.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {client.subscription_tier === 'pro' ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-[#BC562B] text-white text-xs font-semibold">
                      <Crown className="w-3 h-3" /> Pro
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-[#E7E5E4] text-[#57534E] text-xs font-semibold">Free</span>
                  )}
                  <ArrowRight className="w-4 h-4 text-[#737373]" />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-[#E7E5E4] p-8 text-center">
            <Users className="w-8 h-8 text-[#737373] mx-auto mb-3" />
            <p className="text-[#57534E]">No students assigned yet.</p>
            <p className="text-sm text-[#737373] mt-1">Invite clients and assign them to this coach.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCoachDetail;
